import { useListPipelineRunsQuery } from '@/services/api';
import { useProjectScope } from '@/context/ProjectContext';
import { useUrlState } from '@/hooks/useUrlState';
import { Chip } from '@/components/Chip';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { WorkspaceCleanupStatus } from '@/components/WorkspaceCleanupStatus';
import { Link } from 'react-router-dom';
import { formatTimestamp, formatTime, runRequirement } from '@/utils/format';
import type { PipelineRunListQuery } from '@/types/api';

const PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PER_PAGE = PAGE_SIZES[0];
const TIME_RANGES = ['24h', '7d', '30d'] as const;
type TimeRange = (typeof TIME_RANGES)[number] | 'custom';

const STATUSES = ['active', 'completed', 'failed'] as const;
type FilterStatus = (typeof STATUSES)[number] | 'any';

const URL_KEYS = ['status', 'from', 'to', 'range', 'page', 'per_page'] as const;

export function RunHistoryPage(): JSX.Element {
  const { values, set } = useUrlState(URL_KEYS);
  const { project } = useProjectScope();

  const range = (values.range as TimeRange) ?? '30d';
  const status = (values.status as FilterStatus) ?? 'any';
  const page = parseIntSafe(values.page, 1);
  const parsedPerPage = parseIntSafe(values.per_page, DEFAULT_PER_PAGE);
  const perPage = (PAGE_SIZES as ReadonlyArray<number>).includes(parsedPerPage)
    ? parsedPerPage
    : DEFAULT_PER_PAGE;

  const query: PipelineRunListQuery = {
    per_page: perPage,
    page,
    project_id: project.project_id,
    sort: 'started_at_desc'
  };
  if (status !== 'any') query.status = status;
  if (values.from) query.started_from = values.from;
  if (values.to) query.started_to = values.to;

  const { data, isLoading, isFetching, isError, refetch } = useListPipelineRunsQuery(query);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / perPage)) : 1;
  const showingFrom = data && data.items.length > 0 ? (page - 1) * perPage + 1 : 0;
  const showingTo = data ? Math.min(page * perPage, data.total) : 0;

  return (
    <div className="p-6 pb-10">
      <header className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold m-0 mb-0.5">Run history</h1>
        </div>
      </header>

      <div className="bg-panel border border-border rounded-lg overflow-hidden">
        <div className="flex gap-2.5 px-4 py-3 border-b border-border items-end flex-wrap">
          <Field label="Time range">
            <select
              className="h-8 px-2.5 bg-panel-2 border border-border rounded-md text-text text-[12.5px] min-w-[160px] focus:border-accent outline-none"
              value={range}
              onChange={(e) => {
                const next = e.target.value as TimeRange;
                const { from, to } = computeRangeDates(next);
                set({ range: next, from, to, page: '1' });
              }}
              aria-label="Time range"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Custom range</option>
            </select>
          </Field>
          <Field label="Status">
            <select
              className="h-8 px-2.5 bg-panel-2 border border-border rounded-md text-text text-[12.5px] min-w-[160px] focus:border-accent outline-none"
              value={status}
              onChange={(e) => set({ status: e.target.value, page: '1' })}
              aria-label="Status"
            >
              <option value="any">Any status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </Field>
        </div>

        {isLoading || isFetching ? (
          <LoadingTable rows={perPage} />
        ) : isError ? (
          <ErrorState
            title="Failed to load runs"
            body="The pipeline-run service returned an error. Your filters are preserved."
            onRetry={() => void refetch()}
          />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            title="No runs match your filters"
            body="Try widening the time range or changing the status filter. Filters are shared via the URL, so a fresh page won't restore the previous state."
            action={{
              label: 'Clear filters',
              onClick: () =>
                set({ status: undefined, from: undefined, to: undefined, range: undefined, page: '1' })
            }}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[820px]">
              <thead>
                <tr>
                  <Th>Run ID</Th>
                  <Th>Requirement</Th>
                  <Th>Status</Th>
                  <Th>Latest stage</Th>
                  <Th sorted>Started ▼</Th>
                  <Th>Completed</Th>
                  <Th>Workspace</Th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((run) => (
                  <tr key={run.id} className="hover:bg-panel-2 cursor-pointer">
                    <Td>
                      <Link
                        to={`/p/${project.slug}/runs/${encodeURIComponent(run.id)}`}
                        className="text-accent font-mono text-xs"
                      >
                        {run.id}
                      </Link>
                    </Td>
                    <Td>
                      <div className="max-w-[420px]">
                        <div className="truncate" title={runRequirement(run.inputs)}>
                          {runRequirement(run.inputs)}
                        </div>
                        <div className="text-[10.5px] text-text-muted">{run.workflow_name}</div>
                      </div>
                    </Td>
                    <Td>
                      <Chip state={run.state} />
                    </Td>
                    <Td>{run.latest_stage ?? '—'}</Td>
                    <Td>{formatTimestamp(run.started_at)}</Td>
                    <Td>{run.finished_at ? formatTime(run.finished_at) : '—'}</Td>
                    <Td>
                      <WorkspaceCleanupStatus run={run} placeholder={<>—</>} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <nav
              className="px-4 py-3 flex justify-between items-center border-t border-border text-xs text-text-muted"
              aria-label="Pagination"
            >
              <div>
                Showing {showingFrom}–{showingTo} of {data?.total ?? 0} · sorted by started_at desc
              </div>
              <div className="flex gap-1.5 items-center">
                <label htmlFor="run-per-page" className="px-1">
                  Per page
                </label>
                <select
                  id="run-per-page"
                  aria-label="Runs per page"
                  className="px-2 py-1 bg-panel-2 border border-border text-text rounded-md text-xs"
                  disabled={isFetching}
                  value={perPage}
                  onChange={(e) =>
                    set({ per_page: e.target.value, page: '1' })
                  }
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={isFetching || page <= 1}
                  onClick={() => set({ page: String(page - 1) })}
                  className="px-3 py-1 bg-panel-2 border border-border text-text rounded-md text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-border-strong"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  disabled={isFetching || page >= totalPages}
                  onClick={() => set({ page: String(page + 1) })}
                  className="px-3 py-1 bg-panel-2 border border-border text-text rounded-md text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-border-strong"
                >
                  Next →
                </button>
                <span className="px-2 py-1">
                  Page {page} of {totalPages}
                </span>
              </div>
            </nav>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-text-muted">{label}</span>
      {children}
    </label>
  );
}

function Th({ children, sorted = false }: { children: React.ReactNode; sorted?: boolean }): JSX.Element {
  return (
    <th
      className={`text-left px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] border-b border-border-strong bg-panel-2 ${
        sorted ? 'text-text' : 'text-text-muted'
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }): JSX.Element {
  return <td className="px-4 py-2.5 text-[12.5px] border-b border-border">{children}</td>;
}

function LoadingTable({ rows }: { rows: number }): JSX.Element {
  return (
    <div>
      <table className="w-full border-collapse" aria-busy="true">
        <thead>
          <tr>
            <Th>Run ID</Th>
            <Th>Requirement</Th>
            <Th>Status</Th>
            <Th>Latest stage</Th>
            <Th sorted>Started ▼</Th>
            <Th>Completed</Th>
            <Th>Workspace</Th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {Array.from({ length: 7 }, (_, j) => (
                <Td key={j}>
                  <div
                    className="animate-skel"
                    style={{
                      height: 12,
                      borderRadius: 3,
                      background: 'var(--surface-sunken)',
                      width: `${50 + ((i + j) % 4) * 12}%`
                    }}
                  />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseIntSafe(v: string | undefined, fallback: number): number {
  if (!v) return fallback;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function computeRangeDates(range: TimeRange): { from: string | undefined; to: string | undefined } {
  if (range === 'custom') return { from: undefined, to: undefined };
  const now = new Date();
  const fromDate = new Date(now);
  if (range === '24h') fromDate.setHours(now.getHours() - 24);
  else if (range === '7d') fromDate.setDate(now.getDate() - 7);
  else fromDate.setDate(now.getDate() - 30);
  return { from: fromDate.toISOString(), to: now.toISOString() };
}
